/**
 * Shared motion props for popups/modals so every dialog opens and closes the
 * same way: the backdrop fades, the box fades + lifts/scales in.
 *
 * Pass the result of `useReducedMotion()` so users who prefer reduced motion
 * get a plain fade with no movement.
 *
 * Usage:
 *   const reduce = useReducedMotion();
 *   <motion.div className={styles.overlay} {...overlayMotion(reduce)}>
 *     <motion.div className={styles.wrapper} {...modalBoxMotion(reduce)}>…</motion.div>
 *   </motion.div>
 */

export const overlayMotion = (reduce) => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: reduce ? 0 : 0.2, ease: 'easeOut' },
});

export const modalBoxMotion = (reduce) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, scale: 0.92, y: 14 },
    animate: reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 },
    exit: reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 10 },
    transition: { duration: reduce ? 0 : 0.24, ease: [0.16, 1, 0.3, 1] },
});

/**
 * Same as modalBoxMotion, but for dialog boxes that are centred in CSS with
 * `transform: translate(-50%, -50%)`. Framer's animated transform (scale/y)
 * would otherwise overwrite that translate and knock the box off-centre, so we
 * re-apply the centring translate in front of framer's generated transform.
 */
export const modalBoxMotionCentered = (reduce) => ({
    ...modalBoxMotion(reduce),
    transformTemplate: (_values, generated) =>
        generated && generated !== 'none'
            ? `translate(-50%, -50%) ${generated}`
            : 'translate(-50%, -50%)',
});
